import { nutriLookup, NutriValues } from "../data/nutriValues";
import React from "react";
import { round } from "mathjs";
import { cn } from "../lib/utils";

export type NutriComponentProps = {
  name: keyof NutriValues;
  value: number;
  need: number;
  variant: "circular" | "linear";
  fontSize?: string;
  width?: string;
};

const SIZE = 55;
const STROKE = 6;

export const NutriComponent = (props: NutriComponentProps) => {
  const { hasUpperBound, name, color } = nutriLookup[props.name];
  const needRatio = props.need > 0 ? props.value / props.need : 0;
  const fontWeight = hasUpperBound && needRatio >= 1 ? "font-bold" : undefined;
  const clamped = Math.max(0, Math.min(1, needRatio));
  const circumference = 2 * Math.PI * ((SIZE - STROKE) / 2);
  const dashOffset = circumference * (1 - clamped);

  return props.variant == "circular" ? (
    <div className="flex w-full max-w-[68px] flex-col items-center gap-0">
      <span className={cn("text-sm text-ink", fontWeight)}>{name}</span>
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="-rotate-90"
        >
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={(SIZE - STROKE) / 2}
            fill="none"
            stroke="var(--p2p-border)"
            strokeWidth={STROKE}
            strokeOpacity={0.3}
          />
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={(SIZE - STROKE) / 2}
            fill="none"
            stroke={color}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
          />
        </svg>
        <span
          className={cn(
            "absolute inset-0 flex items-center justify-center text-xs text-ink-muted",
            fontWeight,
          )}
        >
          {round(props.value, 0)}
        </span>
      </div>
    </div>
  ) : (
    <div className="flex w-full flex-col items-stretch gap-0">
      <span className={cn("text-sm text-ink", fontWeight)}>{name}</span>
      <div className="h-1.5 w-full rounded-full bg-line/30">
        <div
          className="h-full rounded-full transition-all"
          style={{ backgroundColor: color, width: `${clamped * 100}%` }}
        />
      </div>
      <div className="flex w-full items-center justify-end">
        <span className={cn("text-xs text-ink-muted", fontWeight)}>
          {round(props.value, 0)}/{round(props.need, 0)}
        </span>
      </div>
    </div>
  );
};
